import type { UIPartyCharacter, UIItemContainerKind } from '@sorrem-client/types';
import { ItemTransferProvider, useToast, type ItemMenuBuilder } from '@sorrem-client/ui/components';
import { getStoredLoadout } from '@sorrem/shared';
import { createContext, useCallback, useContext, useMemo, useRef, useState, type ReactNode } from 'react';
import { canMoveItem, toItemSlotRef, type ItemMoveContext } from '@sorrem-client/ui/shared/equipment-slots';
import { resolveItemMenuEntries, toItemMenuItems } from '@sorrem-client/ui/shared/item-menu';
import { applyCharacterHomeMove } from './character-home-item-moves';
import { useCharacterHomeTrade } from './use-character-home-trade';
import { useItemMove } from '@sorrem-client/ui/hooks';

const CharacterHomeCharacterContext = createContext<UIPartyCharacter | null>(null);

type MoveSelection = { itemId: string; container: UIItemContainerKind };
type MoveTarget = { container: UIItemContainerKind; index: number };

/** A move shown before the server's copy of the character has it. */
interface PendingMove {
    readonly id: number;
    readonly characterId: string;
    readonly selection: MoveSelection;
    readonly target: MoveTarget;
    /**
     * The server's copy of the character when the move was saved, or null
     * while saving. A saved move stays shown until a newer copy (which has
     * it) arrives, so it never flickers back meanwhile.
     */
    readonly savedOn: UIPartyCharacter | null;
}

/** Whether a pending move still needs applying on top of `character`. */
function isStillPending(move: PendingMove, character: UIPartyCharacter | null): boolean {
    return character !== null && move.characterId === character.id && (move.savedOn === null || move.savedOn === character);
}

interface CharacterHomeItemTransferProviderProps {
    character: UIPartyCharacter | null;
    children: ReactNode;
}

/**
 * Owns the character home's item-move state so every panel that shows or
 * receives items (the inventory grids and the Loadout frames) shares one
 * selection and one view of the character: the server's latest copy with
 * the moves not yet in it applied on top. Moves show at once and are saved
 * to the server; one it refuses is dropped, and only that one, and the
 * player is told why.
 */
export function CharacterHomeItemTransferProvider({ character, children }: CharacterHomeItemTransferProviderProps): JSX.Element {
    const [pendingMoves, setPendingMoves] = useState<readonly PendingMove[]>([]);
    const nextMoveIdRef = useRef(0);
    // The server's latest copy, for marking which copy a saved move was saved on.
    const characterRef = useRef(character);
    characterRef.current = character;
    const { move } = useItemMove(character?.id ?? null);
    const { showToast } = useToast();

    const resolvedCharacter = useMemo(() => (character === null ? null : pendingMoves
        .filter((pending) => isStillPending(pending, character))
        .reduce((shown, pending) => applyCharacterHomeMove(shown, pending.selection, pending.target), character)
    ), [character, pendingMoves]);

    const handleMoveTo = useCallback((selection: MoveSelection, target: MoveTarget): void => {
        if (!character || !selection.itemId) {
            return;
        }
        nextMoveIdRef.current += 1;
        const id = nextMoveIdRef.current;
        // Moves a newer copy of the character already has are let go here.
        setPendingMoves((moves) => [
            ...moves.filter((pending) => isStillPending(pending, characterRef.current)),
            { id, characterId: character.id, selection, target, savedOn: null },
        ]);
        void move(selection.itemId, toItemSlotRef(target.container, target.index)).then((refusal) => {
            if (refusal !== null) {
                showToast({ message: refusal, severity: 'warning' });
            }
            setPendingMoves((moves) => (refusal !== null
                ? moves.filter((pending) => pending.id !== id)
                : moves.map((pending) => (pending.id === id ? { ...pending, savedOn: characterRef.current } : pending))));
        });
    }, [character, move, showToast]);

    // The server's rules, judged on the loadout as the moves made so far leave it.
    const moveContext = useMemo((): ItemMoveContext | null => (resolvedCharacter === null ? null : {
        equipped: getStoredLoadout(resolvedCharacter),
        carriedItems: resolvedCharacter.carriedItems,
        storedItems: resolvedCharacter.storedItems,
        carriedCapacity: resolvedCharacter.maxCarriedSlots,
    }), [resolvedCharacter]);
    const canMoveTo = useCallback((selection: MoveSelection, target: MoveTarget): boolean => (
        moveContext !== null && canMoveItem(moveContext, selection, target)
    ), [moveContext]);

    // An item's right-click menu (#267): Equip or Unequip, Move to Storage or Carried, Trade, Details.
    const { canTrade, tradeItem } = useCharacterHomeTrade(resolvedCharacter);
    const itemMenu = useCallback<ItemMenuBuilder>((selection, { selectItem }) => {
        if (moveContext === null) {
            return [];
        }
        const entries = resolveItemMenuEntries(moveContext, selection, { hasStorage: true, canTrade: canTrade, canUse: false, canDrop: false });
        const details = (): void => selectItem(selection.itemId, selection.container);
        return toItemMenuItems(entries, {
            move: (target) => handleMoveTo(selection, target),
            details,
            // Shown selected while it asks, so the Inventory tab's panel shows what is being traded.
            trade: () => {
                details();
                void tradeItem(selection.itemId);
            },
        });
    }, [canTrade, handleMoveTo, moveContext, tradeItem]);

    return (
        <CharacterHomeCharacterContext.Provider value={resolvedCharacter}>
            <ItemTransferProvider onMoveTo={handleMoveTo} canMoveTo={canMoveTo} itemMenu={itemMenu}>
                {children}
            </ItemTransferProvider>
        </CharacterHomeCharacterContext.Provider>
    );
}

/** The character with any pending item moves applied, or null while none is loaded. */
export function useCharacterHomeCharacter(): UIPartyCharacter | null {
    return useContext(CharacterHomeCharacterContext);
}
